/**
 * MyParksValue.com Valuation Request Service
 * Queries the myparksvalue_valuation_requests table in Supabase
 */

import { supabase } from '@/lib/supabase';
import type { ValuationRequest, ValuationRequestUpdate, AssessmentSubmission } from '@/types/valuation-request';

const TABLE = 'myparksvalue_valuation_requests';
const ASSESSMENT_TABLE = 'myparksvalue_assessment_submissions';

/** Active (non-archived) requests, newest first */
export async function getValuationRequests(): Promise<ValuationRequest[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('is_archived', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/** Archived requests (soft-deleted), for restore view */
export async function getArchivedValuationRequests(): Promise<ValuationRequest[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('is_archived', true)
    .order('archived_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/** Count of active (non-archived) requests */
export async function getValuationRequestCount(): Promise<number> {
  const { count, error } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('is_archived', false);

  if (error) throw error;
  return count || 0;
}

/** Update editable fields on a request */
export async function updateValuationRequest(
  id: string,
  updates: ValuationRequestUpdate
): Promise<ValuationRequest> {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** Soft-delete: archive a request (data is retained, can be restored) */
export async function archiveValuationRequest(
  id: string,
  archivedBy?: string
): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .update({
      is_archived: true,
      archived_at: new Date().toISOString(),
      archived_by: archivedBy ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
}

/** Restore a previously archived request */
export async function restoreValuationRequest(id: string): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .update({
      is_archived: false,
      archived_at: null,
      archived_by: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
}

/** Fetch assessment submissions matching a given owner email */
export async function getAssessmentsByEmail(email: string): Promise<AssessmentSubmission[]> {
  const { data, error } = await supabase
    .from(ASSESSMENT_TABLE)
    .select('*')
    .ilike('owner_email', email)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}
